import React, { useEffect, useState } from 'react'
import { MapContainer, TileLayer, Marker, Popup, Polygon, useMap } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { parseRestrictedArea } from '../utils/osgb'

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
})

// Custom marker icons for different site statuses
const createCustomIcon = (status) => {
  const colors = {
    'Open': '#28a745',
    'Closed': '#dc3545',
    'Restricted': '#ffc107'
  }
  
  const color = colors[status] || '#6c757d'
  
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        background-color: ${color};
        width: 20px;
        height: 20px;
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 10px;
        color: white;
        font-weight: bold;
      ">
        ${status === 'Open' ? '✓' : status === 'Closed' ? '✗' : '!'}
      </div>
    `,
    iconSize: [26, 26],
    iconAnchor: [13, 13],
    popupAnchor: [0, -13]
  })
}

// Custom marker icons for different location types
const createLocationIcon = (type) => {
  const locationTypes = {
    'takeoff': { color: '#007bff', symbol: '🚁', label: 'T' },
    'landing': { color: '#28a745', symbol: '🛬', label: 'L' },
    'parking': { color: '#6c757d', symbol: '🅿️', label: 'P' }
  }
  
  const config = locationTypes[type] || locationTypes['takeoff']
  
  return L.divIcon({
    className: 'location-marker',
    html: `
      <div style="
        background-color: ${config.color};
        width: 24px;
        height: 24px;
        border-radius: 4px;
        border: 2px solid white;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        color: white;
        font-weight: bold;
      ">
        ${config.label}
      </div>
    `,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -14]
  })
}

// Component to fit map bounds to markers
function MapBounds({ sites, selectedSite, showMultipleLocations }) {
  const map = useMap()
  
  useEffect(() => {
    if (sites.length > 0) {
      const validSites = sites.filter(site => site.latitude && site.longitude)
      
      if (validSites.length === 0) return
      
      if (selectedSite && selectedSite.latitude && selectedSite.longitude && showMultipleLocations) {
        // For detailed view, include all location points
        const allPoints = [[selectedSite.latitude, selectedSite.longitude]]
        
        // Add landing coordinates if available
        if (selectedSite.landing_latitude && selectedSite.landing_longitude) {
          allPoints.push([selectedSite.landing_latitude, selectedSite.landing_longitude])
        }
        
        // Add parking coordinates if available
        if (selectedSite.parking_latitude && selectedSite.parking_longitude) {
          allPoints.push([selectedSite.parking_latitude, selectedSite.parking_longitude])
        }
        
        if (allPoints.length > 1) {
          // Fit bounds to all location points
          const bounds = L.latLngBounds(allPoints)
          map.fitBounds(bounds, { padding: [30, 30] })
        } else {
          // Center on single site with appropriate zoom
          map.setView([selectedSite.latitude, selectedSite.longitude], 14)
        }
      } else if (selectedSite && selectedSite.latitude && selectedSite.longitude) {
        // Center on selected site
        map.setView([selectedSite.latitude, selectedSite.longitude], 12)
      } else if (validSites.length === 1) {
        // Center on single site
        map.setView([validSites[0].latitude, validSites[0].longitude], 10)
      } else {
        // Fit bounds to all sites
        const bounds = L.latLngBounds(
          validSites.map(site => [site.latitude, site.longitude])
        )
        map.fitBounds(bounds, { padding: [20, 20] })
      }
    }
  }, [map, sites, selectedSite, showMultipleLocations])
  
  return null
}

function SitesMap({ 
  sites = [], 
  selectedSite = null, 
  onSiteClick = () => {}, 
  height = '400px',
  showControls = true,
  showMultipleLocations = false
}) {
  const [mapReady, setMapReady] = useState(false)
  
  // Filter sites with valid coordinates
  const validSites = sites.filter(site => 
    site.latitude && 
    site.longitude && 
    !isNaN(site.latitude) && 
    !isNaN(site.longitude)
  )
  
  // Default center (UK center)
  const defaultCenter = [54.5, -2.5]
  const defaultZoom = 6
  
  const formatHeight = (site) => {
    if (site.height_meters) {
      return `${site.height_meters}m`
    }
    return site.height || 'N/A'
  }
  
  const formatCoordinates = (lat, lng) => {
    if (!lat || !lng) return 'N/A'
    return `${lat.toFixed(4)}, ${lng.toFixed(4)}`
  }
  
  return (
    <div className="sites-map-container" style={{ height }}>
      <MapContainer
        center={defaultCenter}
        zoom={defaultZoom}
        style={{ height: '100%', width: '100%' }}
        whenReady={() => setMapReady(true)}
      >
        {/* Topographic tile layer */}
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://www.thunderforest.com/">Thunderforest</a>'
          url="https://tile.thunderforest.com/landscape/{z}/{x}/{y}.png?apikey=your-api-key"
          fallbackUrl="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        {/* Alternative: OpenTopoMap for topographic data */}
        <TileLayer
          attribution='Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, <a href="http://viewfinderpanoramas.org">SRTM</a> | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a> (<a href="https://creativecommons.org/licenses/by-sa/3.0/">CC-BY-SA</a>)'
          url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
          maxZoom={17}
        />
        
        {/* Restricted-area boundary polygons (OSGB coords converted to lat/lng) */}
        {validSites.map(site => {
          const ring = parseRestrictedArea(site.restricted_area)
          if (!ring) return null
          return (
            <Polygon
              key={`restricted-${site.site_id}`}
              positions={ring}
              pathOptions={{
                color: '#b4442f',
                weight: 2,
                fillColor: '#b4442f',
                fillOpacity: 0.18,
              }}
            >
              <Popup>
                <strong>{site.name} - restricted area</strong>
                <br />Do not fly within this boundary.
              </Popup>
            </Polygon>
          )
        })}

        {/* Site markers */}
        {validSites.map(site => (
          <Marker
            key={site.site_id}
            position={[site.latitude, site.longitude]}
            icon={createCustomIcon(site.status)}
            eventHandlers={{
              click: () => onSiteClick(site)
            }}
          >
            <Popup>
              <div className="site-popup">
                <h4 className="popup-title">{site.name}</h4>
                <div className="popup-info">
                  <div className="popup-row">
                    <strong>Direction:</strong> {site.direction}
                  </div>
                  <div className="popup-row">
                    <strong>Height:</strong> {formatHeight(site)}
                  </div>
                  <div className="popup-row">
                    <strong>Status:</strong> 
                    <span className={`status ${site.status.toLowerCase()}`}>
                      {site.status}
                    </span>
                  </div>
                  {site.region && (
                    <div className="popup-row">
                      <strong>Region:</strong> {site.region}
                    </div>
                  )}
                  <div className="popup-row">
                    <strong>Coordinates:</strong> 
                    <span className="coordinates">
                      {formatCoordinates(site.latitude, site.longitude)}
                    </span>
                  </div>
                  {site.lambing && (
                    <div className="popup-warning">
                      🐑 Lambing restrictions may apply
                    </div>
                  )}
                  {site.restricted_area && (
                    <div className="popup-warning">
                      🚫 Restricted area
                    </div>
                  )}
                </div>
                <button 
                  className="btn btn-sm btn-primary popup-button"
                  onClick={() => onSiteClick(site)}
                >
                  View Details
                </button>
              </div>
            </Popup>
          </Marker>
        ))}

        {/* Additional location markers for detailed view */}
        {showMultipleLocations && selectedSite && (
          <>
            {/* Landing marker */}
            {selectedSite.landing_latitude && selectedSite.landing_longitude && (
              <Marker
                key={`${selectedSite.site_id}-landing`}
                position={[selectedSite.landing_latitude, selectedSite.landing_longitude]}
                icon={createLocationIcon('landing')}
              >
                <Popup>
                  <div className="location-popup">
                    <h4 className="popup-title">Landing Area</h4>
                    <div className="popup-info">
                      <div className="popup-row">
                        <strong>Site:</strong> {selectedSite.name}
                      </div>
                      <div className="popup-row">
                        <strong>Coordinates:</strong> 
                        <span className="coordinates">
                          {formatCoordinates(selectedSite.landing_latitude, selectedSite.landing_longitude)}
                        </span>
                      </div>
                      {selectedSite.landing_grid && (
                        <div className="popup-row">
                          <strong>Grid Ref:</strong> {selectedSite.landing_grid}
                        </div>
                      )}
                      {selectedSite.landing_w3w && (
                        <div className="popup-row">
                          <strong>What3Words:</strong> {selectedSite.landing_w3w}
                        </div>
                      )}
                    </div>
                  </div>
                </Popup>
              </Marker>
            )}

            {/* Parking marker */}
            {selectedSite.parking_latitude && selectedSite.parking_longitude && (
              <Marker
                key={`${selectedSite.site_id}-parking`}
                position={[selectedSite.parking_latitude, selectedSite.parking_longitude]}
                icon={createLocationIcon('parking')}
              >
                <Popup>
                  <div className="location-popup">
                    <h4 className="popup-title">Parking</h4>
                    <div className="popup-info">
                      <div className="popup-row">
                        <strong>Site:</strong> {selectedSite.name}
                      </div>
                      <div className="popup-row">
                        <strong>Coordinates:</strong> 
                        <span className="coordinates">
                          {formatCoordinates(selectedSite.parking_latitude, selectedSite.parking_longitude)}
                        </span>
                      </div>
                      {selectedSite.parking_grid && (
                        <div className="popup-row">
                          <strong>Grid Ref:</strong> {selectedSite.parking_grid}
                        </div>
                      )}
                      {selectedSite.parking_w3w && (
                        <div className="popup-row">
                          <strong>What3Words:</strong> {selectedSite.parking_w3w}
                        </div>
                      )}
                    </div>
                  </div>
                </Popup>
              </Marker>
            )}
          </>
        )}
        
        {/* Fit bounds to sites */}
        {mapReady && (
          <MapBounds sites={validSites} selectedSite={selectedSite} showMultipleLocations={showMultipleLocations} />
        )}
      </MapContainer>
      
      {showControls && (
        <div className="map-legend">
          <h4>Site Status</h4>
          <div className="legend-items">
            <div className="legend-item">
              <div className="legend-marker open"></div>
              <span>Open</span>
            </div>
            <div className="legend-item">
              <div className="legend-marker closed"></div>
              <span>Closed</span>
            </div>
            <div className="legend-item">
              <div className="legend-marker restricted"></div>
              <span>Restricted</span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SitesMap